import {Folder, UploadFile} from '@mui/icons-material';
import QueryBuilderOutlinedIcon from '@mui/icons-material/QueryBuilderOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import { Avatar, Box, FormControl, FormHelperText, IconButton, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { UseControllerProps, useController } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getIconAndNameFromExtension } from '../../../Model/iconHelper';
import { MDTypography } from '../MDTypography';

interface IProps extends UseControllerProps {
    height?: number;
    width?: number | string;
    excelOnly?: boolean;
    disabled?: boolean;
    disabledMessage?: string;
}

export default function AppFileDropzone(props: IProps) {

    const allowedFileExtensions = props.excelOnly ? ['xls', 'xlsx', 'xlsm'] : ['jpeg', 'jpg', 'png', 'gif', 'bmp', 'tiff', 'doc', 'docx', 'pdf', 'zip', 'rar', 'xls', 'xlsx', 'xlsm', 'csv'];
    const acceptFileTypes = props.excelOnly ? '.xls, .xlsx, .xlsm' : '.jpeg, .jpg, .png, .gif, .bmp, .tiff, .doc, .docx, .pdf, .zip, .rar, .xls, .xlsx, .xlsm, .csv';
    const errorMessage = props.excelOnly ? `Only '.xls, .xlsx, .xlsm' files are accepted`: `Only '.jpeg, .jpg, .png, .gif, .bmp, .tiff, .doc, .docx, .pdf, .zip, .rar, .xls, .xlsx, .xlsm, .csv' files are accepted`;

    const { fieldState, field } = useController({ ...props, defaultValue: '' });
    const dzStyles = {
        display: 'flex',
        border: 'dashed 3px #eee',
        borderColor: fieldState.error ? "red" : "black",
        borderRadius: '5px',
        alignItems: 'center',
        justifyContent: 'center',
        height: props.height || 200,
        width: props.width || 500
    }

    const dzDisabled = {
        display: 'flex',
        border: 'dashed 3px #eee',
        borderColor: "gray",
        borderRadius: '5px',
        alignItems: 'center',
        justifyContent: 'center',
        height: props.height || 200,
        width: props.width || 500
    }

    const dzActive = {
        borderColor: 'green'
    }

    const onDrop = useCallback(acceptedFiles => {
        const fileName = acceptedFiles[0].name;
        const fileExtension = fileName.split('.').pop().toLowerCase();

        if (allowedFileExtensions.includes(fileExtension)) {
            acceptedFiles[0] = Object.assign(acceptedFiles[0], { preview: URL.createObjectURL(acceptedFiles[0]) })
            field.onChange(acceptedFiles[0]);
        } else {
            toast.error(errorMessage);
        }
    }, [field]);


    const clearFile = () => {
        field.onChange(null);
    };

    const file = field?.value;
    const fileExtension = file?.name?.split('.').pop().toLowerCase();
    const extensionInfo = getIconAndNameFromExtension(fileExtension);
    const icon = extensionInfo ? extensionInfo[0] : null;

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

    return (
        <>
            {!file && props.disabled &&
                <div style={{ width: '100%' }}>
                    <FormControl style={dzDisabled}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <QueryBuilderOutlinedIcon style={{ width: '10vh', height: '10vh', color: 'gray' }}/>
                            <MDTypography variant="h4" style={{ textAlign: 'center', color: 'gray' }}>
                                {props.disabledMessage ?? 'Upload in progress'}
                            </MDTypography>
                        </div>
                    </FormControl>
                </div>
            }
            {!file && !props.disabled &&
                <div {...getRootProps()} style={{ width: '100%' }}>
                    <FormControl style={isDragActive ? { ...dzStyles, ...dzActive } : dzStyles} error={!!fieldState.error}>
                        <input {...getInputProps()} accept={acceptFileTypes} />
                        <div style={
                            isDragActive ? { color: 'green', display: 'flex', flexDirection: 'column', alignItems: 'center' } : { display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <UploadFile style={{ width: '10vh', height: '10vh' }}/>
                            <MDTypography variant="h4" style={{ textAlign: 'center' }}>
                                {props.excelOnly ? `Drag & Drop Excel File or Click Here` : `Drag & Drop File or Click Here`}
                            </MDTypography>
                        </div>
                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                    </FormControl>
                </div>
            }
            {file &&
                <Box display="flex" flexDirection="column" alignItems="center" width={"100%"}>
                    <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
                        <Box position="relative">
                            {icon ? (
                                <img src={`${process.env.PUBLIC_URL}/icons/fileTypes/${icon}`} alt={`icon`} width="200px" height="200px" />
                            ) : (
                                <Box style={{ width: '150px', height: '150px' }} justifyContent="center" alignItems="center" display="flex">
                                    <Avatar>
                                        <Folder />
                                    </Avatar>
                                </Box>
                            )}
                            {!props.disabled &&
                                <IconButton
                                    onClick={clearFile}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        zIndex: 1
                                    }}
                                >
                                    <ClearIcon/>
                                </IconButton>
                            }
                        </Box>
                    </Box>
                    <Typography variant="caption" align="center" pt={1}>{file.name}</Typography>
                </Box>
            }
        </>
    )
}
